import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { profilFastenerFormSchema, type ProfilFastenerFormDataV2 } from "@/types/admin/fastener.types";
import {
  useProfilFasteners,
  useProfilFastener,
  useCreateProfilFastener,
  useUpdateProfilFastener,
  useDeleteProfilFastener,
} from "@/hooks/useProfilFasteners";
import type { ProfilFastener } from "@/api/profilFasteners";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ProfilFastener } | null;

const blank: ProfilFastenerFormDataV2 = { partNo: "", type: "", rivetingForceKn: "" };

export const ProfilFastenersPage = ({
  title = "Profil Fasteners",
  subtitle = "Dataset: Profil Fasteners",
}: {
  title?: string;
  subtitle?: string;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } = useProfilFasteners(params);
  const rows: ProfilFastener[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProfilFastener | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingPartNo = modal?.mode !== "add" ? (modal?.item?.partNo ?? null) : null;
  const detail = useProfilFastener(editingPartNo);

  const createMutation = useCreateProfilFastener();
  const updateMutation = useUpdateProfilFastener();
  const deleteMutation = useDeleteProfilFastener();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfilFastenerFormDataV2>({ resolver: zodResolver(profilFastenerFormSchema) });

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };

  const fillForm = (item: ProfilFastener) => {
    reset({
      partNo: item.partNo,
      type: item.type ?? "",
      rivetingForceKn: item.rivetingForceKn === null ? "" : Number(item.rivetingForceKn),
    });
  };

  const openEdit = (item: ProfilFastener) => { setFormError(null); fillForm(item); setModal({ mode: "edit", item }); };
  const openView = (item: ProfilFastener) => { setFormError(null); fillForm(item); setModal({ mode: "view", item }); };

  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: ProfilFastenerFormDataV2) => {
    setFormError(null);
    const type = form.type?.trim() || null;
    const rivetingForceKn =
      form.rivetingForceKn === "" || form.rivetingForceKn === undefined
        ? null
        : String(form.rivetingForceKn);

    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        { partNo: modal.item.partNo, body: { type, rivetingForceKn } },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save profil fastener.")),
        },
      );
    } else {
      createMutation.mutate(
        { partNo: form.partNo.trim(), type, rivetingForceKn },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create profil fastener.")),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ProfilFastener>[] = [
    { header: "Part No", key: "partNo" },
    { header: "Type", render: (i) => i.type ?? "—" },
    { header: "Riveting Force (kN)", render: (i) => i.rivetingForceKn ?? "—" },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: `Add ${title} record`, edit: `Edit ${title} record`, view: `View ${title} record` };

  return (
    <>
      <PageHeader title={title} subtitle={`${subtitle} · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load profil fasteners{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search part no…"
      />

      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => i.partNo} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <PennField label="Part No" htmlFor="partNo" required={!isView} error={!isView ? errors.partNo?.message : undefined}>
              <PennInput id="partNo" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.partNo} {...register("partNo")} />
            </PennField>
            <PennField label="Type" htmlFor="type" error={!isView ? errors.type?.message : undefined}>
              <PennInput id="type" disabled={isView} error={!isView && !!errors.type} {...register("type")} />
            </PennField>
            <PennField label="Riveting Force (kN)" htmlFor="rivetingForceKn" error={!isView ? errors.rivetingForceKn?.message : undefined}>
              <PennInput id="rivetingForceKn" type="number" step="0.01" disabled={isView} error={!isView && !!errors.rivetingForceKn} {...register("rivetingForceKn")} />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.partNo, {
            onError: (e) => setDeleteError(apiErrorMessage(e, "Failed to delete profil fastener.")),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.partNo}
      />
    </>
  );
};
